import { useEffect, useRef, } from 'react';

// Module-level registry for inputs that own their typed value locally
// (uncontrolled + onBlur-commit). Saving the form means "everything the
// user typed should be committed first" — Save calls flushAll(), each
// registered flusher reads its own current DOM value and dispatches the
// same commit it would on blur. Focus is retained (no DOM .blur() side
// effect on the input the user is in).
//
// flushAll() returns a Promise that resolves after one macrotask — long
// enough for React to commit the setStates the flushers queued. Callers
// just await it before reading state.
//
// Input side: callers attach via useFlushableInput, which returns a ref
// to spread onto their input element.
//
//   const inputRef = useFlushableInput(handleBlur);
//   <TextSmall inputRef={inputRef} onBlurCB={handleBlur} defaultValue={value} />
//
// Save side:
//
//   await flushAll();
//   ...run the save flows...

const flushers = new Set();

export const registerFlusher = (fn) => {
	flushers.add(fn);
	return () => {
		flushers.delete(fn);
	};
};

const runFlushers = () => {
	for (const fn of flushers) {
		try {
			fn();
		} catch (err) {
			console.error('FormFlush flusher threw:', err);
		}
	}
};

export const flushAll = () => {
	runFlushers();
	return new Promise((resolve) => {
		setTimeout(resolve, 0);
	});
};

// Hook for any uncontrolled input that commits its value on blur.
// Returns a ref the caller attaches to the input element; on flushAll()
// it reads the live DOM value (when the input is currently focused) and
// invokes onBlurCB, no .blur() call. onBlurCB should be the same handler
// the input's onBlur fires so flush and blur take the same path.
export const useFlushableInput = (onBlurCB) => {
	const inputRef = useRef(null);
	useEffect(() => {
		return registerFlusher(() => {
			const el = inputRef.current;
			if (!el || document.activeElement !== el) {
				return;
			}
			onBlurCB(el.value.trim());
		});
	}, [ onBlurCB, ]);
	return inputRef;
};
